import React, { Component } from 'react';
import { connect } from 'react-redux'
import { Map } from 'immutable';
import { Modal as BsModal } from 'react-bootstrap';
import xtend from 'xtend';
import moment from 'moment';
import _ from 'underscore';

import Validator from '../../../hocs/Validator';
import Loader from '../../../common/components/notifications/Loader';
import Overlay from '../../../common/components/Overlay';
import HtmlAnchorReplacer from '../../../utils/HtmlAnchorReplacer';

// NOTE:
// We use the same components used in the Event Tab view
// too keep things inline with "DRY"
import General from '../event/tabs/General.wizard';
import Automation from '../event/tabs/Automation.wizard';

import StepBar from './lib/StepBar';
import Overview from './lib/Overview';
import ModeSelect from './lib/ModeSelect';
import EncoderSelect from './lib/EncoderSelect';

import EventMock from './lib/EventMock'
import realignStop from './lib/realignStop'

import CreatedEventModal from './CreatedEventModal';
import RestrictedComponent from '../../../lib/components/form/RestrictedComponent';
import { getContentGroupData } from '../../../redux/data/selectors';
import { track, EVENTS } from '../../../lib/tracking';
import xhr from 'xhr';
import config from '../../../config';
import Scheduling from '../event/tabs/scheduling';
import schema from '../../../routes/permissions/schema';

const NOOP = function () {};
const TIME_FMT = 'HH:mm:ss';
const DATE_FMT = 'YYYY-MM-DD';
const FULL_DT_FMT = DATE_FMT + ' ' + TIME_FMT;

const AvailableSteps = [{
    key: "encoder",
    name: "Select Encoder",
    icon: <i className="fa fa-video-camera"/>,
    component: EncoderSelect,
    hasError: false,
    forLiveOnly: true
}, {
    key: "general",
    name: "Event Details",
    icon: <i className="fa fa-edit"/>,
    component: General,
    hasError: false,
    forLiveOnly: false
}, {
    key: "automation",
    name: "Event Automation",
    icon: <i className="far fa-clock"/>,
    component: Automation,
    hasError: false,
    forLiveOnly: true
}, {
    key: 'scheduling',
    name: 'Schedule Recurring Events',
    icon: <i className='far fa-clock'/>,
    component: Scheduling,
    hasError: false,
    forLiveOnly: false
}];

class CreateEventWizard extends Component {
    constructor (props) {
        super(props);

        this.state = EventMock.getDefaultState()

        this.selectMode = this.selectMode.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.createEvent = this.createEvent.bind(this);
        this.renderActions = this.renderActions.bind(this);
        this.toggleErrorModal = this.toggleErrorModal.bind(this);
        this.onEventsCreated = this.onEventsCreated.bind(this)
        this.getDateTimeAsString = this.getDateTimeAsString.bind(this)
        this.getDateTimeAsMoment = this.getDateTimeAsMoment.bind(this)
        this.requestCategories = this.requestCategories.bind(this)
    }

    // NOTE: React calls this with (prevProps, prevState). These parameters used
    // to be named the other way around, so the `initialEdits` guard below read
    // `prevState.initialEdits` -- a key state never has -- and was therefore
    // true on every update, re-entering setState in a loop.
    async componentDidUpdate (prevProps, prevState) {
        if (this.state.categories === null && !this.state.categoriesLoaded) {
            const data = await this.requestCategories(this.props.clientID)
            this.setState({categoriesLoaded: true, categories: data})
        }
        if (this.props.initialEdits !== prevProps.initialEdits && this.props.initialEdits) {
            this.setState({edits: this.props.initialEdits})
        }
    }

    async componentDidMount() {
        if (this.state.categories === null && !this.state.categoriesLoaded) {
            const data = await this.requestCategories(this.props.clientID)
            this.setState({categoriesLoaded: true, categories: data})
        }
        if (this.props.initialEdits) {
            this.setState({edits: this.props.initialEdits})
        }
    }

    getDateTimeAsMoment (source, date, time) {
        return moment(
            date.format(DATE_FMT) + ' ' + time.format(TIME_FMT),
        )
    }

    getDateTimeAsString (source, date, time) {
        return date.format(DATE_FMT) + ' ' + time.format(TIME_FMT)
    }

    selectMode (mode, hadInvalidData) {
        // NOTE: `deep-extend` returns its target untouched when called with a
        // single argument, so `clone(AvailableSteps)` handed back the module
        // level array itself and every mutation below leaked across mounts.
        let steps = AvailableSteps.map(step => xtend(step));

        // Remove the "Encoder Select" and "Automation"
        // steps for upload, media-based events
        if (mode == 'media') {
            steps = steps.filter(step => !step.forLiveOnly)
        }

        if (hadInvalidData !== false) {
            const grouped = _.groupBy(hadInvalidData, 'group');
            for (var step in steps) {
                const key = steps[step].key;
                if (grouped.hasOwnProperty(key)) {
                    steps[step].hasError = grouped[key];
                }
            }
        }

        this.setState({
            mode: mode,
            steps: steps,
            active: this.state.active || 0,
            invalidModal: hadInvalidData !== false
        });
    }

    handleChange (name, value) {
        this.setState(prevState => {
            let updates = {};
            updates[name] = value;

            // Moving the start drags the stop along with it.
            if (name === 'startDate' || name === 'startTime') {
                const realigned = realignStop(prevState.edits, name, value);
                if (realigned) updates = xtend(updates, realigned);
            }

            const next = {
                edits: xtend(prevState.edits, updates)
            };

            // Reset Error States by default for any changes made.
            // Rebuild the steps rather than mutating them in place, otherwise
            // the already-rendered error modal reads the cleared values.
            if (prevState.steps.some(s => s.hasError !== false)) {
                next.steps = prevState.steps.map(s =>
                    s.hasError === false ? s : xtend(s, { hasError: false })
                );
            }

            return next;
        });
    }

    toggleErrorModal () {
        this.setState({
            invalidModal: !this.state.invalidModal
        });
    }
    onEventsCreated(event) {
        track(EVENTS.EVENT_CREATE)
        if (this && this.props && this.props.showCreatedModal) {
            this.props.showCreatedModal()
        }
        this.setState(state => ({
            successModal: true,
            createdEvent: event
        }))
    }

    async createEvent (ev) {
        const { edits } = this.state;
         // ignore new duplicated create requests while one is pending
        if (this.state.pendingCreate) return
        this.setState({pendingCreate: true})
        let payload = _.pick(edits,
            'title',
            'description',
            'private',
            'notes',
            'streamtext',
            'liveCaptions',
            'autoStart',
            'autoStop',
            'liveToBreak',
            'preroll',
            'assignedEncoder',
            'estRuntime',
            'categories',
        );

        const sdt = moment(
            edits.startDate.format(DATE_FMT) + ' ' + edits.startTime.format(TIME_FMT)
        );

        payload = xtend(payload, {
            // startDateTime: edits.startDateTime.format(FULL_DT_FMT)
            startDateTime: sdt.format(FULL_DT_FMT)
        });

        let generalIsValid = this.props.validate('event', payload);
        let automationIsValid = this.props.validate('automation', xtend(
            payload, {
                stopDate: edits.stopDate,
                stopTime: edits.stopTime
            }
        ));
        if (payload.categories && payload.categories.length) {
            const data = await this.requestCategories(this.props.clientID)
            // validate that all selected categories still exist since we used a single request to get the categories
            // we don't have updates from websockets to categories here
            // we also have to convert the category names into their ids.
            //
            // `_.pick` copies the array *reference* out of state, so writing the
            // ids back into `payload.categories` in place rewrote
            // `state.edits.categories` too -- the category select is keyed by
            // name, so the user's selection silently emptied and every retry
            // failed with "<id> was not found". Build a new array instead.
            const categoryIDs = []
            payload.categories.forEach(categoryName => {
                const match = data.find(cat => cat.categoryName === categoryName)
                if (match) {
                    categoryIDs.push(match.categoryID)
                    return
                }
                // category wasn't found add error
                const err = {
                    key: 'categories',
                    group: 'general',
                    description: `${categoryName} was not found`,
                }
                if (generalIsValid === true) {
                    generalIsValid = [err]
                } else {
                    generalIsValid.push(err)
                }
            })
            payload.categories = categoryIDs
        }

        if (this.props.isInGroup && !(payload.categories && payload.categories.length > 0)) {
            const err = {
                key:'categories',
                group:'general',
                description: 'This event must belong to one of your content group\'s categories.',
                validators: [arr => arr.length > 0]
            }
            if (generalIsValid === true) {
                generalIsValid = [err]
            } else {
                generalIsValid.push(err)
            }
        }

        if (generalIsValid !== true || automationIsValid !== true) {
            this.selectMode(
                this.state.mode,
                [].concat(
                    _.isArray(generalIsValid) ? generalIsValid : []
                ).concat(
                    _.isArray(automationIsValid) ? automationIsValid : []
                ));
                this.setState({pendingCreate: false})
        } else {
            const self = this;

            if (payload.autoStop === true && !!edits.stopDate && !!edits.stopTime) {
                let stopDateTime =  moment([
                    edits.stopDate.format(DATE_FMT),
                    edits.stopTime.format(TIME_FMT)
                ].join(' ')).format(FULL_DT_FMT)

                // Don't save stopDateTime if autoStop is not set
                if (!!stopDateTime === true) {
                    payload = xtend(payload, {
                        stopDateTime: stopDateTime
                    });
                }
            }

            // The CRUD API only accepts `encoderID` so we have to
            // replace the `assignedEncoder` object in state
            if (
                payload.hasOwnProperty('assignedEncoder') &&
                payload.assignedEncoder.hasOwnProperty('encoderID')
            ) {
                payload.encoderID = payload.assignedEncoder.encoderID;
            }

            // API Accepts different variable name for notes
            if (payload.notes) {
                payload.eventNotes = payload.notes
                delete payload.notes
            }

            let validDescription = (
                payload.description !== undefined &&
                payload.description !== '<div><br></div>' &&
                payload.description !== '<div>&nbsp;</div>' &&
                payload.description !== 'null' &&
                payload.description !== null &&
                payload.description !== '<p><br></p>' &&
                payload.description !== '<h1><br></h1>' &&
                payload.description !== '<h2><br></h2>' &&
                payload.description !== '<blockquote><br></blockquote>'
            );

            if (validDescription) {
                payload.description =
                    HtmlAnchorReplacer.parseAndRepaceAnchors(
                        payload.description
                    )
            } else {
                payload.description = null; // API takes null to mean no description
            }

            this.props.showOverlay ? this.props.showOverlay() : {}
            this.setState({ pendingCreate: false })

            const addFunction = this.props.actions.addMultiple ? this.props.actions.addMultiple : this.props.actions.add

            // shape the estRuntime to minutes from hh:mm format
            if (payload.estRuntime) {
                payload.estRuntime = moment.duration(payload.estRuntime).asMinutes()
            } else if (this.state.mode === "encoder" && !payload.estRuntime) {
                payload.estRuntime = 60
            }
            else {
                delete payload.estRuntime
            }
            
            addFunction(payload, function(err, event) {
                if (err) {
                    console.error('error creating event:', err)
                    return err
                }
                if (edits.schedules && edits.schedules.length) {
                    const addNext = function(index = 0) {
                        const nextEventDate = edits.schedules[index].startDateTime
                        const nextEvent = {
                          ...payload,
                          ...edits.scheduledEventProps[nextEventDate],
                        }
                        nextEvent.startDateTime = nextEvent.startDateTime.format('YYYY-MM-DD') + ` ${payload.startDateTime.substring(11)}`
                        addFunction(nextEvent, function (err) {
                            if (err) {
                                console.error('error creating event:', err)
                                return err
                            }
                            if (index < edits.schedules.length - 1) {
                                addNext(index + 1)
                            } else if(index === edits.schedules.length - 1) {
                                self.onEventsCreated(event)
                            }
                        })
                    }
                    addNext()
                } else {
                    self.onEventsCreated(event)
                }
            })
        }
    }

    getTz () {
        if (this.props.timeZone){
            return this.props.timeZone
        }else{
            return this.props.credentials.client.timeZone
        }
    }

    requestCategories (clientID) {
        const request = {
            method: 'post',
            body: JSON.stringify({
                clientID
            }),
            uri: 'https://api.v3.invintus.com/v2/Categories/getCategories',
            headers: {
                'Authorization': config.apiAuthentication.vendorName,
                'Wsc-api-key': config.apiAuthentication.vendorKey,
                'Content-Type': 'application/json'
            }
        }
        return new Promise(function(resolve, reject) {
            xhr(request, (err, resp) => {
                let body
                if (resp.statusCode === 200) {
                    try {
                        body = JSON.parse(resp.body)
                    } catch(err) {
                        console.error('json parse error:', err)
                    }
                    resolve(body.data)
                }
                reject(err || (body && body.hasError))
            })
        })
    }

    render () {
        const { encoders } = this.props
        if (encoders.isResolving || this.state.categoriesLoaded === false) return <Loader message="Fetching Data"/>;

        const { mode, steps, invalidModal, active, edits } = this.state;
        const { startDate, startTime, stopDate, stopTime } = edits
        let componentProps = xtend(this.props, {
            timeZone: this.getTz(),
            event: Map(this.state.event),
            edits: xtend(this.state.edits, {
                startDateTime: this.getDateTimeAsMoment('start', startDate, startTime),
                autoStopTime: this.getDateTimeAsMoment('stop', stopDate, stopTime),
            }),
            mode: 'create-event-wizard',
            eventType: mode === 'media' ? 'media' : 'live', // allows us to conditionally render inputs for media vs live
            handleChange: this.handleChange,
            hasError: (active !== null) ? steps[active].hasError : false,
            categories: this.state.categories,
            isInGroup: this.props.isInGroup,

            // Required action props on some components
            // which wont do anything in this mode as the buttons
            // which trigger them aren't shown
            actions: {
                add: NOOP,
                edit: NOOP,
                delete: NOOP
            }
        });

        let ActiveComponent = (mode != null) ?
                steps[active].component :
                null
        return (<div id="create-event-wizard" className="col-xs-12">
            <BsModal
                show={invalidModal}
                onHide={this.toggleErrorModal}
                bsSize="large"
                animation={false}>
                <BsModal.Header>
                    <BsModal.Title>Form Errors</BsModal.Title>
                </BsModal.Header>

                <BsModal.Body>

                    {this.state.steps.map((step, m) => {

                        if (step.hasError) {
                            return <div key={'step-' + m} className="alert alert-danger">
                                <h5 style={{ textTransform: 'capitalize' }}>
                                    {step.key}
                                </h5>
                                <ul>

                                    {step.hasError.map((err, n) => {

                                        let label = <strong style={{
                                            textTransform: 'capitalize'
                                        }}>{err.key}:</strong>

                                        return _.isArray(err.description) ?
                                            err.description.map((desc, o) =>
                                                <li key={n + o}>{label} {desc}</li>
                                            ) :
                                            <li key={n}>{label} {err.description}</li>
                                    })}
                                </ul>
                            </div>;
                        }
                    })}
                </BsModal.Body>

                {this.state.pendingCreate ? null : <BsModal.Footer>
                    <button className="btn btn-link" onClick={this.toggleErrorModal}>
                        Close
                    </button>
                </BsModal.Footer>}
            </BsModal>

            {/* NOTE: this must stay an overlay. Swapping the wizard body out
                for a loader while `createEvent` awaits the category lookup
                unmounted the step components; remounting them re-fired their
                mount-time `handleChange` calls, which cleared the errors the
                modal was about to render. */}
            <Overlay active={this.props.isResolving || this.state.pendingCreate}/>

            <div className="row clearfix">
                <StepBar {...this.state}
                    selectMode={this.selectMode}
                    selectStep={(index) => {
                        this.setState({
                            active: index
                        });
                    }}/>
            </div>
            <div className="row clearfix" id="content-wrapper">
                <div className="col-md-8">
                    <div className="panel">
                        <div className="panel-body">
                            <ModeSelect {...this.state} selectMode={this.selectMode}/>
                            {mode != null ?
                                <ActiveComponent {...componentProps}/> :
                                null}
                        </div>
                        <div className="panel-footer">
                            {this.renderActions(mode, steps, active)}
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <Overview {...this.state}/>
                </div>
            </div>

            {this.state.successModal ? <CreatedEventModal {...this.state}
                mode="route-create"
                reset={ev => {
                    ev && ev.preventDefault()
                    this.setState(EventMock.getDefaultState())
                }}
                visible={true}
            /> : null}
        </div>);
    }

    renderActions (mode, steps, active) {
        if (steps.length) {
            const self = this;
            const { pendingCreate } = this.state

            function renderNavButton (direction) {

                let show = false;
                let text = direction.capitalizeWord();
                if (direction == 'back') {
                    text = <span><i className="fa fa-caret-left"/> {text}</span>;
                    show = (active !== 0);
                } else {
                    text = <span>{text} <i className="fa fa-caret-right"/></span>;
                    show = active !== steps.length - 1;
                }

                return show ? (<button data-testid={`create-event-wizard-${direction.toLowerCase()}`} className="btn btn-primary"
                    onClick={(ev) => {
                        ev.preventDefault();
                        self.setState({
                            active: (direction == 'back') ?
                                active - 1 :
                                active + 1
                        });
                    }}>
                        {text}
                </button>) : null;
            }

            return (
              <div className="row">
                <div className="col-sm-6">
                  <button
                    className="btn btn-danger"
                    onClick={(ev) => {
                      ev && ev.preventDefault();
                      this.setState(EventMock.getDefaultState());
                    }}
                  >
                    Reset Wizard
                  </button>
                </div>
                <div className="col-sm-6 text-right">
                  <div className="btn-group">
                    {renderNavButton("back")}
                    {renderNavButton("next")}
                  </div>
                  {active == steps.length - 1 ? (
                      <RestrictedComponent permissionCheck={schema.EVENT.CREATE}>
                        <button
                          data-testid="create-event-wizard-create-event"
                          style={{
                            marginLeft: "1rem",
                          }}
                          disabled={pendingCreate}
                          onClick={this.createEvent}
                          className="btn btn-success"
                        >
                          Create Event
                        </button>
                      </RestrictedComponent>
                  ) : null}
                </div>
              </div>
            );
        }
    }
}

CreateEventWizard.propTypes = {};

const mapStateToProps = state => {
    let { contentGroups } = getContentGroupData(state)
    const clientID = state.data.user.clientId
    const isInGroup = !!contentGroups.length
    return { isInGroup, clientID }
}

const WizardWithValidators = Validator(
    connect(mapStateToProps)(CreateEventWizard)
);

export default WizardWithValidators;
